import { useCallback, useEffect, useRef, useState } from 'react';

import { read, save } from '@/utils/async-storage';

const CURRENCY_GUIDANCE_KEY = 'should_currency_guidance_appear';

const LOG_TAG = '[Currency Guidance]';

/**
 * Hook to manage the first-time currency guidance modal
 * Returns whether to show the modal and a function to mark it as shown
 */
export const useCurrencyGuidance = () => {
    const [shouldShow, setShouldShow] = useState(false);
    const alreadyHandledInteraction = useRef(false);
    const isInitialized = useRef(false);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            // Defer the check to avoid blocking the main thread during navigation
            requestAnimationFrame(() => {
                read(CURRENCY_GUIDANCE_KEY)
                    .then(hasBeenShown => {
                        // If the key does not exist, or the value is false, show the guidance
                        if (hasBeenShown === null || (typeof hasBeenShown === 'boolean' && !hasBeenShown)) {
                            setShouldShow(true);
                        }
                    })
                    .catch(error => {
                        console.error('Error checking currency guidance flag:', error);
                    });
            });
        }
    }, []);

    const markAsShown = useCallback(async () => {
        if (!alreadyHandledInteraction.current) {
            try {
                await save(CURRENCY_GUIDANCE_KEY, true);
                alreadyHandledInteraction.current = true;
                setShouldShow(false);
            } catch (error) {
                console.error(
                    LOG_TAG,
                    `Error saving currency guidance ${CURRENCY_GUIDANCE_KEY} flag in async storage:`,
                    error
                );
            }
        }
    }, []);

    return { shouldShow, markAsShown };
};
